// src/components/usuarios/UsuarioRoleEditor.tsx
import React, { useState } from 'react';
import { api } from '../../api/client';

interface Usuario {
    id: number;
    email: string;
    nombre: string;
    apellido: string;
    tipo_usuario: string;
    roles: string[];
    fecha_creacion: string;
    email_verificado: boolean;
    cuenta_bloqueada: boolean;
    // Token usage tracking
    total_tokens_used: number;
    total_openai_tokens: number;
    total_embedding_tokens: number;
    token_limit_monthly: number | null;
    token_limit_daily: number | null;
    tokens_last_reset: string | null;
}

interface Props {
    usuario: Usuario;
    onSave: () => void;
    onCancel: () => void;
}

const AVAILABLE_ROLES = ['admin', 'shop', 'normas'];

export default function UsuarioRoleEditor({ usuario, onSave, onCancel }: Props) {
    const [roles, setRoles] = useState<string[]>(usuario.roles);
    const [cuentaBloqueada, setCuentaBloqueada] = useState(usuario.cuenta_bloqueada);
    const [tokenLimitMonthly, setTokenLimitMonthly] = useState<number | null>(usuario.token_limit_monthly);
    const [tokenLimitDaily, setTokenLimitDaily] = useState<number | null>(usuario.token_limit_daily);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleRole = (role: string) => {
        if (roles.includes(role)) {
            setRoles(roles.filter(r => r !== role));
        } else {
            setRoles([...roles, role]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const updatedUser = {
                ...usuario,
                roles,
                cuenta_bloqueada: cuentaBloqueada,
                token_limit_monthly: tokenLimitMonthly,
                token_limit_daily: tokenLimitDaily,
            };

            await api.usuarios.update(usuario.id, updatedUser);
            onSave();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Error al actualizar usuario';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">
                    Editar Roles de Usuario
                </h2>

                <div className="mb-4 p-3 bg-gray-50 rounded">
                    <div className="text-sm text-gray-600">Usuario</div>
                    <div className="font-medium text-gray-900">
                        {usuario.nombre} {usuario.apellido}
                    </div>
                    <div className="text-sm text-gray-600">{usuario.email}</div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Roles Asignados
                        </label>
                        <div className="flex flex-row flex-wrap gap-2">
                            {AVAILABLE_ROLES.map((role) => (
                                <label
                                    key={role}
                                    className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={roles.includes(role)}
                                        onChange={() => toggleRole(role)}
                                        disabled={loading}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-900 capitalize">
                                        {role}
                                    </span>
                                    {role === 'admin' && (
                                        <span className="ml-2 text-xs text-red-600">
                                            (Acceso total)
                                        </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                                type="checkbox"
                                checked={cuentaBloqueada}
                                onChange={(e) => setCuentaBloqueada(e.target.checked)}
                                disabled={loading}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                                Bloquear cuenta
                            </span>
                            <span className="ml-auto text-xs text-gray-500">
                                El usuario no podrá iniciar sesión
                            </span>
                        </label>
                    </div>

                    {/* Token Limits Section */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Límites de Tokens
                        </label>
                        <div className="space-y-3">
                            {/* Current Usage Display */}
                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                                <div className="text-xs font-medium text-blue-800 mb-1">Uso Actual</div>
                                <div className="text-sm text-blue-900">
                                    Total: {(usuario.total_tokens_used || 0).toLocaleString()} tokens
                                </div>
                                <div className="text-xs text-blue-700 mt-1">
                                    OpenAI: {(usuario.total_openai_tokens || 0).toLocaleString()} | Embeddings: {(usuario.total_embedding_tokens || 0).toLocaleString()}
                                </div>
                            </div>
                            <div className="flex flex-row flex-wrap gap-3">
                                {/* Monthly Limit */}
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Límite Mensual
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            min="0"
                                            value={tokenLimitMonthly || ''}
                                            onChange={(e) => setTokenLimitMonthly(e.target.value ? parseInt(e.target.value) : null)}
                                            placeholder="Sin límite"
                                            disabled={loading}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {tokenLimitMonthly && (
                                            <button
                                                type="button"
                                                onClick={() => setTokenLimitMonthly(null)}
                                                className="ml-2 px-3 py-2 text-sm text-red-600 hover:text-red-800 whitespace-nowrap"
                                            >
                                                Quitar
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {/* Daily Limit */}
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Límite Diario
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            min="0"
                                            value={tokenLimitDaily || ''}
                                            onChange={(e) => setTokenLimitDaily(e.target.value ? parseInt(e.target.value) : null)}
                                            placeholder="Sin límite"
                                            disabled={loading}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {tokenLimitDaily && (
                                            <button
                                                type="button"
                                                onClick={() => setTokenLimitDaily(null)}
                                                className="ml-2 px-3 py-2 text-sm text-red-600 hover:text-red-800 whitespace-nowrap"
                                            >
                                                Quitar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            type="submit"
                            disabled={loading || roles.length === 0}
                            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={loading}
                            className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 disabled:bg-gray-300 transition-colors"
                        >
                            Cancelar
                        </button>
                    </div>

                    {roles.length === 0 && (
                        <div className="mt-3 text-sm text-red-600 text-center">
                            ⚠️ Debe seleccionar al menos un rol
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
